import {
  Tooltip,
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Heading,
  HStack,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { InfoOutlineIcon } from '@chakra-ui/icons'
import { useQuery } from '@tanstack/react-query'
import { FiZap } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { monitoringService } from '@/services/admin/monitoringService'
import { SolanaSignerLaneMonitor } from '@/components/monitoring/SolanaSignerLaneMonitor'

const TOOLTIP_MODIFIERS = [
  {
    name: 'preventOverflow',
    options: {
      padding: 8,
      altAxis: true,
    },
  },
  {
    name: 'flip',
    options: {
      padding: 8,
    },
  },
]

const formatMetric = (value?: number | null, digits = 1): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '--'
  }
  return `${value.toFixed(digits)}%`
}

const formatLatency = (value?: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '--'
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(2)}s`
  }
  return `${Math.round(value)}ms`
}

const formatInteger = (value?: number | null): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '--'
  }
  return Math.round(value).toLocaleString()
}

type MetricLabelProps = {
  label: string
  help: string
  mutedText: string
}

function MetricLabel({ label, help, mutedText }: MetricLabelProps) {
  return (
    <Box as="span" display="inline-flex" alignItems="center" gap={1}>
      <Text as="span">{label}</Text>
      <Tooltip
        label={help}
        hasArrow
        placement="top"
        modifiers={TOOLTIP_MODIFIERS}
        openDelay={200}
        pointerEvents="none"
        maxW="280px"
        whiteSpace="normal"
        textAlign="left"
      >
        <Box
          as="span"
          color={mutedText}
          cursor="help"
          aria-label={help}
          display="inline-flex"
          alignItems="center"
          lineHeight={1}
          flexShrink={0}
        >
          <InfoOutlineIcon boxSize={3.5} />
        </Box>
      </Tooltip>
    </Box>
  )
}

type SectionHelpProps = {
  help: string
  mutedText: string
}

function SectionHelp({ help, mutedText }: SectionHelpProps) {
  return (
    <Tooltip
      label={help}
      hasArrow
      placement="top"
      modifiers={TOOLTIP_MODIFIERS}
      openDelay={200}
      pointerEvents="none"
      maxW="280px"
      whiteSpace="normal"
      textAlign="left"
    >
      <Box
        as="span"
        color={mutedText}
        cursor="help"
        aria-label={help}
        display="inline-flex"
        alignItems="center"
        lineHeight={1}
        flexShrink={0}
      >
        <InfoOutlineIcon boxSize={4} />
      </Box>
    </Tooltip>
  )
}

type SolanaMonitorProps = {
  isActive: boolean
}

export function SolanaMonitor({ isActive }: SolanaMonitorProps) {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const cardBg = useColorModeValue('white', 'gray.700')
  const mutedText = useColorModeValue('gray.600', 'gray.300')

  const { data, isLoading } = useQuery({
    queryKey: ['monitoring', 'metrics', currentEnv],
    queryFn: () => monitoringService.getMetrics(currentEnv),
    refetchInterval: 30 * 1000,
    staleTime: 15 * 1000,
  })

  const solana = data?.solana
  const unavailable = !isLoading && !solana

  return (
    <VStack spacing={4} align="stretch">
      <SolanaSignerLaneMonitor isActive={isActive} />

      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <HStack>
            <FiZap />
            <Heading size="md">{t('monitoring.solana.title')}</Heading>
            <SectionHelp
              help={t('monitoring.solana.subtitle')}
              mutedText={mutedText}
            />
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          {unavailable && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {t('monitoring.solana.metricsUnavailable')}
              </AlertDescription>
            </Alert>
          )}
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4}>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.solana.initializationSuccessRate')}
                    help={t('monitoring.solana.help.initializationSuccessRate')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber>
                  {isLoading ? '--' : formatMetric(solana?.initializationSuccessRate)}
                </StatNumber>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.solana.avgDetectionLatency')}
                    help={t('monitoring.solana.help.avgDetectionLatency')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber>
                  {isLoading ? '--' : formatLatency(solana?.avgDetectionLatencyMs)}
                </StatNumber>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.solana.avgConfirmationLatency')}
                    help={t('monitoring.solana.help.avgConfirmationLatency')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber>
                  {isLoading ? '--' : formatLatency(solana?.avgConfirmationLatencyMs)}
                </StatNumber>
              </Stat>
            </GridItem>
            <GridItem>
              <Stat>
                <StatLabel>
                  <MetricLabel
                    label={t('monitoring.solana.rpcErrorRate')}
                    help={t('monitoring.solana.help.rpcErrorRate')}
                    mutedText={mutedText}
                  />
                </StatLabel>
                <StatNumber fontSize="xl">
                  {isLoading ? '--' : formatMetric(solana?.rpcErrorRate)}
                </StatNumber>
              </Stat>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      <Card bg={cardBg}>
        <CardHeader pb={2}>
          <Heading size="sm">{t('monitoring.solana.outcomesTitle')}</Heading>
        </CardHeader>
        <CardBody pt={1}>
          <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(5, 1fr)' }} gap={4}>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.solana.settlementSuccessRate')}
                  help={t('monitoring.solana.help.settlementSuccessRate')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading ? '--' : formatMetric(solana?.settlementSuccessRate)}
              </Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.solana.refundSuccessRate')}
                  help={t('monitoring.solana.help.refundSuccessRate')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading ? '--' : formatMetric(solana?.refundSuccessRate)}
              </Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.solana.treasurySuccessRate')}
                  help={t('monitoring.solana.help.treasurySuccessRate')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading ? '--' : formatMetric(solana?.treasurySuccessRate)}
              </Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.solana.rpcSlotLag')}
                  help={t('monitoring.solana.help.rpcSlotLag')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" mt={1}>
                {isLoading ? '--' : formatInteger(solana?.rpcSlotLag)}
              </Heading>
            </Box>
            <Box>
              <Text color={mutedText} fontSize="sm">
                <MetricLabel
                  label={t('monitoring.solana.latestRpcSlot')}
                  help={t('monitoring.solana.help.latestRpcSlot')}
                  mutedText={mutedText}
                />
              </Text>
              <Heading size="sm" fontSize="lg" mt={1}>
                {isLoading ? '--' : formatInteger(solana?.latestRpcSlot)}
              </Heading>
            </Box>
          </Grid>
        </CardBody>
      </Card>
    </VStack>
  )
}

export default SolanaMonitor
